import React from 'react';
import { Stack, Typography } from '@mui/material';

import { isCertificateFieldHidden } from '@repo/types';

import { TermInfo } from 'components/UI/TermInfo';
import { formatedDate, parseApiDate } from 'utils/date';
import { border, detailLabelSx, font, space } from 'settings/tokens';

interface ProductDetailsPanelProps {
  className?: string;
  certificate: any;
  /**
   * When set, adds an `Owned since` row.
   *
   * Passed only for the OWNER's own view. "Current owner: you, since <date>" was
   * a whole card on the Provenance screen, where the identity half is something
   * the reader already knows — the only part carrying information was the date.
   * As a fact row it costs one line, sits with the other facts, and is on the
   * first screen instead of behind a tap.
   */
  ownedSince?: string | null;
}

/**
 * The certificate's facts, as a borderless key/value table.
 *
 * VERA-315 Phase 3. This was a grey bordered card stacking each label ABOVE its
 * value, which cost roughly double the height and — the part that matters — put
 * the values in a ragged left column interleaved with labels, so the eye cannot
 * run down them. Chrono24's spec detail is the model copied here: label left in
 * muted grey, value right, no card, no fill, a hairline between rows.
 *
 *     Certificate ID:            77307325-d99a-456e-9ab3-236b287d4b78
 *     Product ID:                                 LV-JACKET-MONO-2023
 *     Manufacture date:                              15 January 2024
 *
 * The card is also chrome competing with the photograph, which is the one thing
 * on this page that should be loud (Phase 5 removes the rest of it).
 *
 * ## Rows this deliberately does NOT carry
 *
 * **Product URL.** A full URL right-aligned in a 375px column wraps to three
 * broken lines, and `View product` sits directly beneath this table as the
 * affordance for it. One link, not two, and the button is the one that reads as
 * clickable.
 *
 * **Owner and Status**, which the redesign's Phase 3 listed. Owner has its own
 * block below (`Current owner` → `OwnerProvenanceCard`) with an avatar and a
 * since-date this table cannot show, and status is carried by the chip beside
 * the title plus, when it is not `active`, its own callout. Both would be a
 * second rendering of something already on the page.
 */
export const ProductDetailsPanel: React.FC<ProductDetailsPanelProps> = ({
  certificate,
  ownedSince,
}) => {
  /*
   * TWO manufacture dates exist and this row shows the more specific one.
   *
   * `certificates.manufactured_at` is per-ITEM (VERA-924) — the date this individual
   * object was made. `products.manufactured_at` is per-SKU — the date the product line
   * was made, shared by every certificate under it. When both are set the per-item date
   * is the truthful answer for the thing in the reader's hand, so it wins.
   *
   * This panel used to read the SKU-level field ALONE, which had two consequences and
   * the quieter one was worse: an item whose per-item date was set but whose product had
   * none rendered NO manufacture row at all — the row is dropped when the value is falsy,
   * so a populated field looked exactly like an empty one. Nothing reported it.
   *
   * `?? ` and not `||` deliberately: absence here is `null` (both columns are nullable
   * timestamps), and `??` is what the three provenance use-cases already use for the same
   * pair — `get-public-provenance-history.use-case.ts:150` and its admin/customer twins.
   * This panel was the only reader in the codebase NOT applying that fallback.
   *
   * `parseApiDate` rather than handing the raw value to `formatedDate`: the cert-level
   * column is selected in raw SQL on the certificate aggregate, and raw-SQL columns arrive
   * as Postgres' `2026-07-20 05:39:29` rather than ISO. WebKit returns Invalid Date for
   * that, `formatedDate` has no NaN guard, and this app's primary runtime is iOS Safari —
   * so the unguarded version throws `RangeError` and takes the page down. See utils/date.
   */
  const manufacturedAt = parseApiDate(
    certificate?.manufactured_at ?? certificate?.product?.manufactured_at
  );

  /*
   * `termId` is the glossary entry a row's `(i)` explains, and only the first row has
   * one. "Certificate" is the word this whole page is an instance of and the one a
   * first-time reader is least likely to have a precise meaning for — the glossary's own
   * definition is mostly about correcting the natural wrong guess, that a certificate
   * covers a product line rather than this single object. "Product ID", "Manufacture
   * date" and "Owned since" are ordinary English and have no entry; inventing one so
   * every row could carry a glyph would put four info buttons on a four-row table, which
   * is how an affordance stops being read at all.
   */
  /*
   * VRG-104 — the brand's own `text` custom fields, appended AFTER the standard rows.
   *
   * After, not interleaved, and that is the decision rather than an implementation
   * detail. The rows above are Vera's: the certificate id, the SKU, the dates. They are
   * the part of this table a reader is being asked to trust, and a brand-authored row
   * sitting between two of them would borrow that standing. Appending keeps the spine
   * intact and gives the brand a bounded slot under it — the same shape the whole
   * feature is built on (fixed record, bounded brand slots), one table down.
   *
   * Only `text` lands here. `rich` and `link` render in the brand block below the
   * description, because neither is a short right-aligned value: a paragraph in this
   * column would wrap to five lines against a label, and a link needs its host beside
   * it.
   *
   * The `visible` filter is belt-and-braces. The API already drops hidden fields from
   * every certificate projection (`resolveVisibleCustomFields`) — deliberately, so a
   * hidden value never reaches the wire at all, which is where the guarantee has to
   * live. This re-check only covers a payload from somewhere else, and it is not what
   * keeps an internal cost code off a customer's screen.
   *
   * A field with no value is DROPPED rather than rendered as `—`. An em-dash on a
   * standard row means "Vera has no value for this"; on an optional field the brand
   * added and left blank it means nothing at all, and a certificate reads better
   * without a row that says nothing.
   */
  const customTextRows = (
    Array.isArray(certificate?.product?.custom_fields)
      ? certificate.product.custom_fields
      : []
  )
    .filter(
      (field: any) =>
        field &&
        field.visible !== false &&
        (field.type ?? 'text') === 'text' &&
        String(field.value ?? '').trim() &&
        String(field.label ?? '').trim()
    )
    .map((field: any) => ({
      label: `${String(field.label).trim()}:`,
      value: String(field.value).trim(),
    }));

  /*
   * VRG-788 — the brand's per-SKU hides, applied at RENDER.
   *
   * Not by removing the values from the payload, which is how `custom_fields` does it:
   * `sku` feeds the certificate LIST's title fallback (certificateUtils.ts), the teaser,
   * the chip list and three transfer screens, and `description` has its own surface — so
   * nulling them server-side would break the owner's own screens over a setting about
   * the certificate's details table. The consequence, stated rather than hidden: a hidden
   * SKU is still on the wire, and still used as a display handle on those owner surfaces.
   *
   * `Certificate ID` is absent from this list on purpose. It is Vera's, locked, and the
   * curation UI shows it as locked rather than omitting it — see
   * CERTIFICATE_LOCKED_FIELDS for why each of the four is.
   *
   * This also makes the owner's PUBLIC PREVIEW faithful for free: the rule is the same
   * for every viewer, so applying it here means preview and visitor see one answer.
   */
  const hidden = certificate?.product?.hidden_certificate_fields;

  const rows = [
    { label: 'Certificate ID:', value: certificate?.id, termId: 'certificate' },
    isCertificateFieldHidden(hidden, 'sku')
      ? null
      : { label: 'Product ID:', value: certificate?.product?.sku },
    manufacturedAt && !isCertificateFieldHidden(hidden, 'manufactured_at')
      ? { label: 'Manufacture date:', value: formatedDate({ date: manufacturedAt }) }
      : null,
    ownedSince ? { label: 'Owned since:', value: formatedDate({ date: ownedSince }) } : null,
    ...customTextRows,
  ].filter(Boolean) as { label: string; value?: string; termId?: string }[];

  return (
    <Stack>
      {rows.map((row, idx) => (
        <Stack
          // Index-keyed, not label-keyed: a brand is free to name a custom field
          // "Product ID", and two children with one key is a React warning plus a row
          // that fails to re-render when the list changes.
          key={`${idx}-${row.label}`}
          direction='row'
          justifyContent='space-between'
          alignItems='baseline'
          sx={{
            gap: `${space.lg}px`,
            py: `${space.md}px`,
            // Hairline between rows, none under the last — the table ends where
            // its content ends rather than drawing a box around itself.
            // `!important` because `#vera-shadow-root * { border-color: … }` has
            // ID specificity and silently discards an sx border colour without
            // it (see settings/tokens.ts).
            ...(idx < rows.length - 1
              ? { borderBottom: `1px solid ${border.subtle} !important` }
              : null),
          }}
        >
          {/* The `(i)` sits with the LABEL, not in the value column — it explains the
              word, so it belongs beside the word. `flexShrink: 0` moves to the pair so
              the label still never truncates in favour of the value. */}
          <Stack direction='row' alignItems='center' spacing={0.5} sx={{ flexShrink: 0 }}>
            <Typography sx={detailLabelSx}>{row.label}</Typography>
            {row.termId && <TermInfo termId={row.termId} />}
          </Stack>
          {/* `overflowWrap: anywhere` WITHOUT `wordBreak: break-all`. The value
              that wraps is a 36-character UUID, and break-all cut it mid-token,
              leaving a five-character orphan alone on the second line. Normal
              wrapping takes the hyphens as break opportunities and splits it
              into two balanced lines instead; `anywhere` stays as the backstop
              for a long value with no hyphen at all. */}
          <Typography
            sx={{
              fontSize: font.label,
              lineHeight: 1.5,
              textAlign: 'right',
              overflowWrap: 'anywhere',
            }}
          >
            {row.value || '—'}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

export default ProductDetailsPanel;
